import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "@/lib/utils";

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0",
  {
    variants: {
      variant: {
        default: "bg-[--teal] text-[--teal-foreground] shadow-soft hover:opacity-90",
        destructive: "bg-destructive text-destructive-foreground shadow-sm hover:bg-destructive/90",
        outline:
          "border border-input bg-background text-foreground hover:bg-[--gold-soft] hover:border-[--gold] hover:text-[--navy]",
        secondary: "bg-secondary text-secondary-foreground hover:bg-[--gold-soft]",
        ghost: "hover:bg-[--gold-soft] hover:text-[--navy]",
        link: "text-[--teal] underline-offset-4 hover:underline",
        gold: "bg-gradient-gold text-gold-foreground shadow-gold hover:opacity-95 font-semibold tracking-wide",
        dark: "bg-[--navy] text-white hover:bg-[--teal] transition-colors",
        teal: "bg-[--teal] text-white hover:bg-[--navy]",
        hero: "bg-gradient-gold text-gold-foreground shadow-gold hover:scale-[1.02] hover:shadow-elegant font-semibold uppercase tracking-widest transition-all",
      },
      size: {
        default: "h-9 px-4 py-2",
        sm: "h-8 rounded-md px-3 text-xs",
        lg: "h-11 rounded-md px-8",
        icon: "h-9 w-9",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  },
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>, VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : "button";
    return (
      <Comp className={cn(buttonVariants({ variant, size, className }))} ref={ref} {...props} />
    );
  },
);
Button.displayName = "Button";

export { Button, buttonVariants };
